import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EndUser, endUserInitials } from "../../components/EndUser";
import { EmptyLiveMark, EmptySearchMark, ErrorState, Skeleton } from "../../components/States";
import { ConfirmDialog, DataTable, PageHeader, RowMenu, useToast, type Column } from "../../components/ui";
import { useAuth } from "../../auth/store";
import { formatCityRegion } from "../../lib/country";
import { formatDuration, formatRelative, privacyLabel } from "../../lib/format";
import { liveApi, liveKeys, type LiveSessionItem } from "./api";
import { CAPABILITIES, CAPABILITY_LABEL } from "./model";
import { useCan } from "../../auth/roles";
import { allowsFeature, useOrgEntitlements } from "../billing/entitlements";
import { liveUnavailableMessage, useOrgLimits } from "../billing/limits";

/** Opening or joining a live session requires the plan, a feature grant, or gifted minutes. The list itself does not. */
export function LiveAccess({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const ent = useOrgEntitlements(auth.currentOrgId);
  if (!auth.currentOrgId || ent.isPending) return <Skeleton />;
  if (ent.isError) return <div className="card section"><ErrorState error={ent.error} retry={() => void ent.refetch()} /></div>;
  if (!allowsFeature(ent.data, "live_assist")) return <LiveTease orgId={auth.currentOrgId} />;
  return children;
}

function LiveTease({ orgId }: { orgId: string }) {
  const can = useCan();
  return (
    <>
      <PageHeader title="Live sessions" back={<Link to="/live">← Live</Link>} />
      <div className="card">
        <div className="empty" data-testid="live-tease">
          <EmptyLiveMark />
          <h3>Upgrade to view a session or join it</h3>
          {can.billing && (
            <Link to="/orgs/$org/billing" params={{ org: orgId }} className="btn primary empty-action">
              See plans
            </Link>
          )}
        </div>
      </div>
    </>
  );
}

/** The help code a user reads out. The server only ever looks up six digits. */
const CODE_LENGTH = 6;

/** Same rules as Sessions `q`: case-insensitive contains on URL, title, and identity; exact session id. */
export function matchesLiveQuery(s: LiveSessionItem, q: string): boolean {
  const term = q.trim();
  if (!term) return true;
  if (s.id === term) return true;
  const needle = term.toLowerCase();
  return [s.url, s.title, s.endUserId, s.endUserName].some((v) => v?.toLowerCase().includes(needle));
}

export function LivePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [looking, setLooking] = useState(false);
  const [qInput, setQInput] = useState("");
  const qc = useQueryClient();
  const can = useCan();
  const ent = useOrgEntitlements(auth.currentOrgId);
  const included = ent.isSuccess && allowsFeature(ent.data, "live_assist");
  const q = useQuery({ queryKey: liveKeys.sessions(auth.currentProjectId), queryFn: liveApi.sessions, enabled: !!auth.currentProjectId });
  // Assisting needs the plan to carry Live Assist and the month's minutes to
  // be under the cap. Sessions still list so support sees who is online.
  const limits = useOrgLimits(auth.currentOrgId);
  const unavailable = liveUnavailableMessage(limits);
  const [closing, setClosing] = useState<LiveSessionItem | null>(null);
  const close = useMutation({
    mutationFn: (sessionId: string) => liveApi.close(sessionId),
    onSuccess: () => {
      setClosing(null);
      toast.push("success", "Session closed");
      void qc.invalidateQueries({ queryKey: liveKeys.sessions(auth.currentProjectId) });
    },
    onError: (e: Error) => toast.push("error", e.message || "Could not close the session"),
  });

  const lookup = async () => {
    if (code.length !== CODE_LENGTH) return;
    setLooking(true);
    try {
      const r = await liveApi.lookup(code);
      void navigate({ to: "/live/$sessionId", params: { sessionId: r.sessionId } });
    } catch (e) {
      toast.push("error", (e as Error).message || "Code not found");
    } finally {
      setLooking(false);
    }
  };

  const rows = useMemo(() => (q.data ?? []).filter((s) => matchesLiveQuery(s, qInput)), [q.data, qInput]);

  const columns: Column<LiveSessionItem>[] = [
    { key: "user", header: "User", className: "live-user-cell", render: (s) => <EndUser id={s.endUserId} name={s.endUserName} place={formatCityRegion(s)} avatar /> },
    { key: "session", header: "Session", render: (s) => <strong className="mono">{s.id}</strong> },
    { key: "page", header: "Page", render: (s) => <span className="mono small live-page-cell" title={s.url}>{s.title || s.url}</span> },
    {
      key: "capture",
      header: "Capture",
      render: (s) =>
        s.privacyMode ? (
          <span className={`badge ${s.privacyMode === "strict" ? "ok" : "neutral"}`} title="Privacy mode for this visit">
            {privacyLabel(s.privacyMode)}
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "live",
      header: "Live for",
      render: (s) => (
        <span className="cell-stack">
          <strong>{formatDuration(Math.max(0, Date.now() - new Date(s.liveSince).getTime()))}</strong>
          <small title={s.lastSeenAt}>{formatRelative(s.lastSeenAt)}</small>
        </span>
      ),
    },
    {
      // Consent only exists while an agent is in the session, so it belongs to
      // the agent rather than to a column of its own that is empty most of the time.
      key: "agent",
      header: "Assisted by",
      render: (s) =>
        s.agentName ? (
          <span className="cell-stack">
            <strong>{s.agentName}</strong>
            <span className="live-consent-badges">
              {CAPABILITIES.filter((c) => s.consent?.[c]).map((c) => (
                <span key={c} className="badge ok small">
                  {CAPABILITY_LABEL[c]}
                </span>
              ))}
              {!CAPABILITIES.some((c) => s.consent?.[c]) && <span className="muted small">no consent yet</span>}
            </span>
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: "assist",
      header: "",
      align: "right",
      render: (s) =>
        included ? (
          <span className="live-row-actions">
            <RowMenu label={`Actions for ${s.title || s.url}`} testId={`row-menu-${s.id}`}>
              {(!unavailable || s.assistId) && (
                <Link to="/live/$sessionId" params={{ sessionId: s.id }} role="menuitem" data-testid={`assist-${s.id}`}>
                  {s.assistId ? "Open assist" : "Assist"}
                </Link>
              )}
              <button
                role="menuitem"
                className="danger"
                title="Stop listing this session. It comes back if the SDK is still streaming."
                onClick={() => setClosing(s)}
                data-testid={`close-${s.id}`}
              >
                Close session
              </button>
            </RowMenu>
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Live sessions"
        actions={
          included ? (
            <Link to="/live/history" className="btn ghost">
              Assist history
            </Link>
          ) : undefined
        }
      >
        <div className="toolbar live-list-toolbar">
          {included && (
          <form
            className="live-code"
            onSubmit={(e) => {
              e.preventDefault();
              void lookup();
            }}
            autoComplete="off"
          >
            <label htmlFor="help-code">Help code</label>
            <input
              id="help-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
              placeholder="123456"
              maxLength={CODE_LENGTH}
              inputMode="numeric"
              pattern="\d{6}"
              autoComplete="off"
              disabled={!!unavailable}
            />
            <button className="btn" type="submit" disabled={!!unavailable || looking || code.length !== CODE_LENGTH}>
              Open
            </button>
          </form>
          )}
          <input
            className="input live-search"
            placeholder="Search URL, title, user…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            aria-label="Search live sessions"
            autoComplete="off"
          />
        </div>
      </PageHeader>
      {ent.isSuccess && !included && (
        <div className="banner warn" role="status" data-testid="live-locked">
          Upgrade to view a session or join it.
          {can.billing && (
            <>
              {" "}
              <Link to="/orgs/$org/billing" params={{ org: auth.currentOrgId ?? "" }}>
                See plans
              </Link>
            </>
          )}
        </div>
      )}
      {included && unavailable && (
        <div className="banner warn" role="status" data-testid="live-unavailable">
          {unavailable}
          {can.billing && limits?.live.reason !== "disabled" && (
            <>
              {" "}
              <Link to="/orgs/$org/billing" params={{ org: auth.currentOrgId ?? "" }}>
                Open billing
              </Link>
            </>
          )}
        </div>
      )}
      {q.isLoading && <Skeleton />}
      {q.isError && <ErrorState error={q.error} retry={() => void q.refetch()} />}
      {q.data && (
        <div className="card live-list-card">
          {rows.length === 0 ? (
            <div className="empty">
              {qInput.trim() ? <EmptySearchMark /> : <EmptyLiveMark />}
              <h3>{qInput.trim() ? "No live sessions match that search." : "No live sessions right now."}</h3>
            </div>
          ) : (
            <>
              <DataTable columns={columns} rows={rows} rowKey={(s) => s.id} ariaLabel="Live sessions" />
              <div className="session-card-list live-card-list" aria-label="Live sessions">
                {rows.map((s) => {
                  const place = formatCityRegion(s);
                  return (
                  <div className="session-card live-card-item" key={s.id} data-testid={`live-card-${s.id}`}>
                    <span className="session-card-head">
                      <span className="session-card-who">
                        <span className={`session-avatar${s.endUserId ? "" : " anon"}`} aria-hidden="true">
                          {s.endUserId ? endUserInitials(s.endUserId, s.endUserName) : "?"}
                        </span>
                        <span className="session-card-ident">
                          <strong className={s.endUserName ? undefined : "mono"} title={s.endUserName ?? s.endUserId}>
                            {s.endUserName ?? s.endUserId ?? "Anonymous"}
                          </strong>
                          {place && <span className="end-user-place" title={place}>{place}</span>}
                          {s.endUserName && s.endUserId && <span className="end-user-id mono">{s.endUserId}</span>}
                          <span className="session-card-when">
                            <span className="badge live">● Live</span>
                            <span title={s.liveSince}>{formatDuration(Math.max(0, Date.now() - new Date(s.liveSince).getTime()))}</span>
                            <span aria-hidden="true">·</span>
                            <span title={s.lastSeenAt}>seen {formatRelative(s.lastSeenAt)}</span>
                          </span>
                        </span>
                      </span>
                    </span>
                    <span className="session-card-session">
                      <span className="session-card-title">Page</span>
                      <span className="session-card-id mono" title={s.url}>{s.title || s.url}</span>
                    </span>
                    {(s.privacyMode || s.agentName) && (
                      <span className="session-card-badges">
                        {s.privacyMode && <span className={`badge ${s.privacyMode === "strict" ? "ok" : "neutral"}`}>{privacyLabel(s.privacyMode)}</span>}
                        {s.agentName && <span className="badge info">Assisted by {s.agentName}</span>}
                        {s.agentName && CAPABILITIES.filter((c) => s.consent?.[c]).map((c) => (
                          <span key={c} className="badge ok small">{CAPABILITY_LABEL[c]}</span>
                        ))}
                      </span>
                    )}
                    {included && (
                    <span className="live-card-actions">
                      {(!unavailable || s.assistId) && (
                        <Link to="/live/$sessionId" params={{ sessionId: s.id }} className="btn primary">
                          {s.assistId ? "Open assist" : "Assist"}
                        </Link>
                      )}
                      <button type="button" className="btn ghost" onClick={() => setClosing(s)}>
                        Close
                      </button>
                    </span>
                    )}
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      <ConfirmDialog
        open={!!closing}
        danger
        title="Close session"
        confirmLabel={close.isPending ? "Closing…" : "Close session"}
        busy={close.isPending}
        onConfirm={() => closing && close.mutate(closing.id)}
        onClose={() => setClosing(null)}
      >
        <p>{closing?.title || closing?.url} stops being listed. It comes back if the page is still open.</p>
      </ConfirmDialog>
    </>
  );
}
